import type { IFigureDto } from '../models/IFigureDto';

type PageWithFigures = {
  data: IFigureDto[];
  totalCount?: number;
} & Record<string, unknown>;

type InfiniteDataWithFigures<TPage extends PageWithFigures = PageWithFigures> = {
  pages: TPage[];
} & Record<string, unknown>;

export function applyFigureUpdatedToInfiniteData<TData extends InfiniteDataWithFigures>(
  oldData: TData | undefined,
  updatedFigure: IFigureDto,
): TData | undefined {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      data: page.data.map((f) =>
        f.imageId === updatedFigure.imageId
          ? {
              ...f,
              customerFavorite: updatedFigure.customerFavorite,
              customerComment: updatedFigure.customerComment,
              customerMetadata: updatedFigure.customerMetadata,
              headerComment: updatedFigure.headerComment,
            }
          : f,
      ),
    })),
  };
}

export function applyFigureRemovedToInfiniteData<TData extends InfiniteDataWithFigures>(
  oldData: TData | undefined,
  params: {
    removedFigureId: number;
    removedFromCollectionId: number;
    currentCollectionId: number;
  },
): TData | undefined {
  if (!oldData) return oldData;

  const { removedFigureId, removedFromCollectionId, currentCollectionId } = params;

  return {
    ...oldData,
    pages: oldData.pages.map((page) => {
      const had = page.data.some((f) => f.imageId === removedFigureId);
      const mapped = page.data.map((f) =>
        f.imageId === removedFigureId
          ? {
              ...f,
              collectionMemberships: Object.fromEntries(
                Object.entries(f.collectionMemberships).filter(([k]) => Number(k) !== removedFromCollectionId),
              ),
            }
          : f,
      );

      const filtered =
        removedFromCollectionId === currentCollectionId ? mapped.filter((f) => f.imageId !== removedFigureId) : mapped;

      const nextTotalCount =
        removedFromCollectionId === currentCollectionId && had
          ? Math.max(0, (page.totalCount || 0) - 1)
          : page.totalCount;

      return {
        ...page,
        data: filtered,
        totalCount: nextTotalCount,
      };
    }),
  };
}

